import React from 'react';
import './Cip.css'; // Assuming this is the patent pending badge based on project layout
import WhatCipDoes from './WhatCipDoes';
import ProductFeatures from './ProductFeatures';
import RequestInfo from '../RequestInfo/RequestInfo';
import Divider from '../VerticalsWePower/Divider';
import { Helmet } from 'react-helmet-async';
import siteConfig from '../../configuration/siteConfiguration';
import { desc } from 'framer-motion/client';
import QuoteMark from '../VerticalsWePower/QuoteMark';

const isMobile = window.innerWidth < 768;
const isDesktop = window.innerWidth >= 1024;

const featureDataOld = [
  {
    id: 1,
    title: 'Adapts to Any Industry',
    icon: 'adapts-to-any-industry.svg',
  },
  {
    id: 2,
    title: 'Automates Complex Workflows',
    icon: 'automates-complex-workflows.svg',
  },
  {
    id: 3,
    title: 'Secures Your Data by Design',
    icon: 'secures-your-data-by-design.svg',
  },
  {
    id: 4,
    title: 'Powers Domain-Aware Copilots',
    icon: 'powers-domain-aware-copilots.svg',
  },
  {
    id: 5,
    title: 'Integrates With Your Stack',
    icon: 'integrates-with-your-stack.svg',
  },
  {
    id: 6,
    title: 'Predicts Risks Before They Happen',
    icon: 'predicts-risks-before-they-happen.svg',
  },
  {
    id: 7,
    title: 'Enables Multi-Agent Collaboration',
    icon: 'enables-multi-agent-collaboration.svg',
  },
  {
    id: 8,
    title: 'Learns From Your Ecosystem',
    icon: 'learns-from-your-ecosystem.svg',
  },
];

const featureData = [
  {
    id: 1,
    title: 'Security & Trust Layer',
    description: 'Zero-trust access, encrypted pipelines, audit-ready logs — compliance from day one.',
    icon: 'secures-your-data-by-design.svg',
  },
  {
    id: 2,
    title: 'Modular Agentic Framework',
    description: 'Plug-and-play AI blocks like OCR, summarization, copilots — deployable anywhere.',
    icon: 'enables-multi-agent-collaboration.svg',
  },
  {
    id: 3,
    title: 'Predictive Dashboards',
    description: 'Forecast outcomes, track risks, and drive insight-led decisions.',
    icon: 'predicts-risks-before-they-happen.svg',
  },
  {
    id: 4,
    title: 'Low-Code Config Builder',
    description: 'Empower business users to orchestrate AI — no dev required.',
    icon: 'learns-from-your-ecosystem.svg',
  },
  {
    id: 5,
    title: 'Domain Intelligence Layer',
    description: 'AI that speaks your industry’s language — not just your data.',
    icon: 'powers-domain-aware-copilots.svg',
  },
  {
    id: 6,
    title: 'Semantic Reasoning Engine',
    description: 'Retrieval-augmented generation with contextual memory and accuracy.',
    icon: 'automates-complex-workflows.svg',
  },
  {
    id: 7,
    title: 'Multi-Agent Collaboration',
    description: 'Intelligent handoffs between copilots, departments, and workflows.',
    icon: 'enables-multi-agent-collaboration.svg',
  },
  {
    id: 8,
    title: 'Integration-First Design',
    description: 'Connects to your CRM, DMS, HMS, ERP, ECM and more — out of the box.',
    icon: 'integrates-with-your-stack.svg',
  }
];

const Cip = () => {
  const leftFeatures = featureData.slice(0, 4);
  const rightFeatures = featureData.slice(4, 8);

  return (
    <>
      <Helmet>
        <title>CIP™ - Cross-Industry Intelligence Platform</title>
        <meta name="description" content="CIP™ is a next-gen, patent pending cross-industry AI platform enabling real-time, predictive intelligence across various sectors." />
        <meta name="keywords" content="CIP, Cross-Industry Intelligence Platform, AI, Legal, Healthcare, BFSI, ESG & Energy, Government" />
        <link rel="canonical" href="https://www.cognitva.ai/cip" />
        <meta property="og:title" content="CIP™ - Cross-Industry Intelligence Platform" />
        <meta property="og:description" content="CIP™ is a next-gen, patent pending cross-industry AI platform enabling real-time, predictive intelligence across various sectors." />
        <meta property="og:url" content="https://www.cognitva.ai/cip" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.cognitva.ai/images/cip-logo.png" />
        <meta property="og:image:alt" content="CIP Logo" />
        <meta property="og:site_name" content="Cognitva Global Technologies Private Limited" />
      </Helmet>
      <section className="cip-section">
        <div className="cip-content-left">
          <div style={isDesktop ? { position: 'relative', top: '+140px' } : {}}>
            <p className="gb-section-badge"><strong>—</strong> COGNITVA CIP™ - OUR CORE AI INFRASTRUCTURE</p>
            <div className="cip-title-logo-wrapper">
              <div className="gb-section-title">
                Enterprise-grade. Patent-pending. Built for scale.
              </div>
              <div className="cip-logo-container">
                <img src="/images/cip.svg" alt="CIP Logo" className="cip-logo" />
              </div>
            </div>
            <p className="gb-section-desc">
              CIP™ is Cognitva’s flagship product — a modular, cross-industry intelligence platform that delivers real-time, predictive AI across Legal, Healthcare, Finance, ESG, Energy, and Regulatory sectors.
              With plug-and-play modules for legal research, automation, compliance, analytics, and multi-agent collaboration, CIP turns fragmented workflows into intelligent ecosystems — accelerating decisions, reducing risk, and scaling impact.
            </p>
            <div className="gb-left-quote-section">
              <QuoteMark />
              <span className="gb-left-quote-text">Build once. Adapt anywhere.</span>
            </div>
          </div>
        </div>
        {isMobile && <hr className="ci-separator" />}
        <div className="cip-content-right">
          <div style={isDesktop ? { position: 'relative', top: '-50px' } : {}}>
            <div className='feature-columns-title'>Smart Product Features</div>
            <div className="feature-columns-animate">

              <div className="feature-column feature-column-down">
                {leftFeatures.concat(leftFeatures).map((feature, idx) => (
                  <div class="feature-card" key={'down-' + idx + '-' + feature.id}>
                    <div class="feature-card-header">
                      <img src={`${siteConfig.assetBaseUrl}/icons/cognitva-cip/hero-section---cross-industry-intelligence-platform/${feature.icon}`}
                        alt="feature icon"
                        width="56px"
                        height="56px"
                      />
                      <div class="feature-card-title">{feature.title}</div>
                    </div>
                    <div class="feature-card-description">
                      {feature.description}
                    </div>
                  </div>
                ))}
              </div>
              <div className="feature-column feature-column-up">
                {rightFeatures.concat(rightFeatures).map((feature, idx) => (
                  <div class="feature-card" key={'up-' + idx + '-' + feature.id}>
                    <div class="feature-card-header">
                      <img src={`${siteConfig.assetBaseUrl}/icons/cognitva-cip/hero-section---cross-industry-intelligence-platform/${feature.icon}`}
                        alt="feature icon"
                        width="56px"
                        height="56px"
                      />
                      <div class="feature-card-title">{feature.title}</div>
                    </div>
                    <div class="feature-card-description">
                      {feature.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>


        </div>
      </section>
      <WhatCipDoes />
      <ProductFeatures />
      <Divider />
      <RequestInfo />
    </>
  );
};

export default Cip;
